import * as React from 'react';
import { isNil, isArray, isEmpty, isString, isNumber, omitBy, mapValues } from 'lodash';
import styles from './DividedColumnDetails.styles';
import {
  DividedColumnDetailsSectionColumn,
  DividedColumnDetailsRenderer,
  DividedColumnDetailsState,
  ValueSelection,
  DividedColumnDetailsProps,
} from './DividedColumnDetails.types';
import DividedColumnDetailsDropdown from './DividedColumnDetailsDropdown';
import { processApiParams } from '../StyleEdit.utils';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import DependentPropDropdown from './DependentPropDropdown';
import InputGeneric from 'src/common-ui/components/Inputs/InputGeneric/InputGeneric';
import { classes } from 'typestyle';
import { decorateTextByClasses, Renderer } from 'src/utils/Domain/Renderer';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import { DividedColumnDetailsDropdownRenderer } from './DividedColumnDetailsDropdownRenderer';
import { AsyncCellState } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { getDataKeyAsyncState } from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { executeCalculation } from 'src/utils/LibraryUtils/MathUtils';
import * as globalMath from 'mathjs';
import WeekRangePicker from 'src/common-ui/components/WeekRange/WeekRangePicker';
import { NumberedWeek } from 'src/common-ui/components/WeekRange/WeekRangePicker.interface';
import { createRowSelectionObj } from '../StyleEditSection/Editors/Editors.utils';
import { makeModalOverlay } from '../StyleEditSection/Editors/Editor.styles';
import IntegerEditor from '../StyleEditSection/Editors/IntegerEditor/IntegerEditor';
import { IntegerEditorParams } from '../StyleEditSection/Editors/IntegerEditor/IntegerEditor.types';
import { DaysRangeListResponse } from 'src/types/Scope';
import stylePaneStyles from 'src/components/HistoryStylePane/HistoryStylePane.styles';
import ImageCropperModal from 'src/components/ImageCropper/ImageCropperModal/ImageCropperModal';
import CenteredImage from 'src/components/CenteredImage/CenteredImage';
import noImagePath from 'src/common-ui/images/noimage.jpg';
import { resolvePath } from 'src/cdn';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';

const noImage = resolvePath(noImagePath);

export const nullDetected = (value: string[]) => {
  return value.some((str) => {
    if (isNil(str)) {
      return true;
    }

    // check for 'Undefined' as a string value
    return isString(str) ? str.toLowerCase().trim() === 'undefined' : false;
  });
};

export function getTooltipTitle(value: string | string[]): string | JSX.Element {
  if (!isArray(value)) {
    return value;
  }

  if (isEmpty(value) || nullDetected(value)) {
    return ''; // don't show tooltip for empty multi-selections
  }

  return (
    <React.Fragment>
      {value.map((title) => (
        <div key={title}>{title}</div>
      ))}
    </React.Fragment>
  );
}

export const checkKeyAsyncState = (dataIndex: string, data: BasicPivotItem | undefined) => {
  if (data != null) {
    let strippedDI = dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
    return getDataKeyAsyncState(data, strippedDI);
  } else {
    return AsyncCellState.Idle;
  }
};

export const renderTextForDividedColumnDetails = (text: string, renderer: DividedColumnDetailsRenderer | undefined) => {
  const numericValue = Number(text); // below expects to work with number types only
  if (renderer == null || Renderer[renderer] == null) {
    return text;
  } else {
    return !isNaN(numericValue) ? Renderer[renderer](numericValue) : text;
  }
};

export const createSavePayload = (
  styleId: string,
  dataIndex: string,
  value: ValueSelection['value'],
  updateCoordinateMap: DividedColumnDetailsProps['updateCoordinateMap'],
  data: DividedColumnDetailsState['data'],
): GranularEditPayloadItem | undefined => {
  if (data == null) {
    return;
  }
  // build coords, either from the passed‐in map or default to product:id
  const coords = updateCoordinateMap
    ? omitBy(
        mapValues(updateCoordinateMap, (field) => data[field]),
        isNil,
      )
    : { product: styleId }; // fallback to what /product endpoint used
  // This fun mess removes that silly wrapped colon stuff (eg: attribute:<x>:id)
  // This is the more concise version: .replace(/(?:^.*?:)?([^:]*)(?::.*)?/, '$1')
  const formattedDataIndex = dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
  // build payload item
  const payload: GranularEditPayloadItem = {
    coordinates: coords,
    [formattedDataIndex]: value,
  };

  return payload;
};

/**
 * Get the display label(s) from the prefetched options
 */
export const getDisplayValueForDividedColumnDetails = (
  dataIndex: string,
  rawValue: string | string[],
  multiSelect: boolean | undefined,
  dropdownOptions: Record<string, Suggestion[]>,
): string | string[] => {
  const opts: Suggestion[] = dropdownOptions[dataIndex] || [];

  // look up matching options based on multi or single
  if (multiSelect) {
    const ids = Array.isArray(rawValue)
      ? rawValue
      : // default empty values "{}" to empty array,
        // an empty value run through array split below produces [""]
        // which will cause a blank chip to be rendered
        rawValue === '{}'
        ? []
        : rawValue.replace('{', '').replace('}', '').split(',');

    const matches = ids.map((id) => {
      const match = opts.find((opt) => String(opt.value) === String(id));
      return match ? match.label : String(id);
    });
    return matches;
  } else {
    const id = String(rawValue ?? '');
    const match = opts.find((opt) => String(opt.value) === id);
    return match ? match.label : id;
  }
};

export const handleDividedColumnDetailsDropdownChange = (dropdownDetails: {
  selection: ValueSelection;
  item: DividedColumnDetailsSectionColumn;
  styleId: string;
  data: DividedColumnDetailsState['data'];
  dependentCalcs: DividedColumnDetailsState['dependentCalcs'];
  mathInstance: globalMath.MathJsStatic;
  updateCoordinateMap: DividedColumnDetailsProps['updateCoordinateMap'];
}):
  | {
      updatedData: BasicPivotItem | undefined;
      updatedValue: ValueSelection['value'];
      payloads: GranularEditPayloadItem[];
    }
  | undefined => {
  // if the selection is undefined/null, don't go through this (dependent dropdowns without options or clearing)
  if (isNil(dropdownDetails.selection)) return;
  const { styleId } = dropdownDetails;
  const { data, dependentCalcs } = dropdownDetails;
  const { dataIndex } = dropdownDetails.item;

  let updatedData: BasicPivotItem | undefined;
  const payloads: GranularEditPayloadItem[] = [];

  // assume data update will succeed...on failure 'rollback' to original values
  const nameDataIndex = dataIndex.replace(':id', ':name');
  const selection = dropdownDetails.selection;
  const updatedValue = isNumber(selection.value) ? (!isNaN(selection.value) ? selection.value : 0) : selection.value;

  if (!isNil(data)) {
    // update attribute (id and name fields)
    // assuming at this level we will never be updating id, name, description on style
    // only members/attributes now above

    updatedData = { ...data };
    updatedData[dataIndex] = updatedValue;
    updatedData[nameDataIndex] = selection.label;
  }

  // check if dataindex is used in any dependent calculations, and if it is, update those calcs
  const calcKeys = Object.keys(dependentCalcs);
  if (calcKeys.length > 0 && updatedData) {
    calcKeys.forEach((key) => {
      let updateCalculation = false;
      const calcObj = dependentCalcs[key];
      const params = calcObj.params;

      if (params) {
        for (const p in params) {
          if (dataIndex === params[p] || nameDataIndex === params[p]) {
            updateCalculation = true;
          }
        }
      }
      if (updateCalculation && updatedData) {
        const getDataFromKey = (key2: string) => {
          if (!updatedData) return { rowNodeFound: false, data: undefined };
          return {
            rowNodeFound: true,
            data: updatedData[key2],
          };
        };
        const newValue = executeCalculation(dropdownDetails.mathInstance, calcObj, getDataFromKey);
        const keyName = key.replace(':id', ':name');
        updatedData[key] = newValue;
        updatedData[keyName] = newValue;
        const dependentPayload = createSavePayload(
          styleId,
          key,
          newValue,
          dropdownDetails.updateCoordinateMap,
          dropdownDetails.data,
        );
        dependentPayload != null && payloads.push(dependentPayload);
      }
    });
  }
  return { updatedData, updatedValue, payloads };
};

// wraps EditableGrid's IntegerEditor so we can reuse its min/max/nullable/percent logic
const IntegerEditorInline = ({
  value,
  inputParams,
  onClose,
  onCommit,
}: {
  value: string | number | null;
  inputParams?: IntegerEditorParams;
  onClose: () => void;
  onCommit: (value: ValueSelection['value']) => void;
}) => {
  const editorRef = React.useRef<IntegerEditor>(null);

  const commit = () => {
    const newValue = editorRef.current ? editorRef.current.getValue() : value;
    onClose();
    if (newValue !== value) {
      onCommit(newValue);
    }
  };

  return (
    <div className={classes(styles.dataItem, styles.dataTextInput)} onBlur={commit}>
      <IntegerEditor
        ref={editorRef}
        passedInt={value as number}
        value={value}
        inputParams={inputParams}
        regularPosition={true}
        stopEditing={commit}
      />
    </div>
  );
};

export const columnConfigToEditableField = (columnDetails: {
  item: DividedColumnDetailsSectionColumn;
  componentIndex: number;
  styleId: string;
  data: DividedColumnDetailsState['data'];
  dropdownOptions: DividedColumnDetailsState['dropdownOptions'];
  dependentCalcs: DividedColumnDetailsState['dependentCalcs'];
  mathInstance: globalMath.MathJsStatic;
  updateCoordinateMap: DividedColumnDetailsProps['updateCoordinateMap'];
  onCloseCallback: () => void;
  onClickCallback: (rendererKey: string) => void;
  openDropdownKey: DividedColumnDetailsState['openDropdownKey'];
  handleDropdownChange: (selection: ValueSelection, item: DividedColumnDetailsSectionColumn) => void;
  // week range data + handler for the 'rangePickerEditor' inputType,
  // only supplied by hosts that support it (currently just `HistoryStylePane`)
  rangePickerData?: {
    mergedRangeList: DaysRangeListResponse;
    allowFrom: string;
    allowTo: string;
  };
  handleRangePickerChange?: (range: NumberedWeek[], item: DividedColumnDetailsSectionColumn) => void;
}) => {
  const { styleId } = columnDetails;
  const {
    text,
    dataIndex,
    dataApi,
    renderer,
    inputType,
    editable,
    asCsv,
    multiSelect,
    valueType,
    mandatory,
    hidden,
    allowEmptyOption,
    concatOptionValues,
    inputParams,
    disabledBy,
  } = columnDetails.item;
  const { data, dropdownOptions } = columnDetails;
  const value = !isNil(data) ? data[dataIndex] : '';
  const key = `${dataIndex}-${columnDetails.componentIndex}`;
  let cellLoading = checkKeyAsyncState(dataIndex, data) != AsyncCellState.Idle;
  if (hidden) return null;

  const renderDCDUneditableComponent = () => {
    const formattedText = renderTextForDividedColumnDetails(text, renderer);
    return (
      <div className={classes(styles.dataItem, styles.dataText)} key={key} data-qa="non-editable-column-value">
        <div className={styles.dataText}>{formattedText}</div>
      </div>
    );
  };

  if (!editable && inputType !== 'dependentDropdown') {
    return renderDCDUneditableComponent();
  }

  let isDisabled = false;
  if (!isNil(disabledBy) && !isNil(data) && !isNil(data[disabledBy])) {
    isDisabled = true;
  } else if (cellLoading) {
    isDisabled = true;
  }
  const rawValue = value;
  // check for empty vals
  const isEmptySingle = !multiSelect && (rawValue == null || rawValue === '');
  const isEmptyMulti = multiSelect && Array.isArray(rawValue) && rawValue.length === 0;
  // if the field is marked mandatory, it’s "missing"
  const isMissing = mandatory && (isEmptySingle || isEmptyMulti);
  // grab the pre-fetched list of valid options for this column
  const opts: Suggestion[] = dropdownOptions[dataIndex] || [];
  const isUnmapped =
    !multiSelect &&
    typeof rawValue === 'string' &&
    opts.length > 0 &&
    !opts.some((opt) => String(opt.value) === rawValue);

  const isInvalid = isMissing ?? isUnmapped;

  switch (inputType) {
    case 'dropdown': {
      if (!dataApi) return <div />;
      const allowEmpty = isNil(allowEmptyOption) ? true : allowEmptyOption;
      const concatValues = isNil(concatOptionValues) ? false : concatOptionValues;
      const displayValue = getDisplayValueForDividedColumnDetails(dataIndex, rawValue, multiSelect, dropdownOptions);
      const tooltipTitle = !multiSelect ? '' : getTooltipTitle(displayValue);
      const isActive = columnDetails.openDropdownKey === key;
      const processedDataApi = processApiParams(dataApi, data ?? {});

      return isDisabled || !isActive ? (
        <DividedColumnDetailsDropdownRenderer
          id={key}
          key={key}
          displayValue={displayValue}
          inputParams={inputParams}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          onClick={columnDetails.onClickCallback}
          tooltip={tooltipTitle}
        />
      ) : (
        <Tooltip
          className={styles.dataItem}
          data-qa="real-multiselect-tooltip"
          key={key}
          title={tooltipTitle}
          placement="right"
        >
          <DividedColumnDetailsDropdown
            dataQa={`select-${key}`}
            styleId={styleId}
            dataApi={processedDataApi}
            asCsv={asCsv}
            multiSelect={multiSelect}
            value={value}
            handleDropdownChange={(selection) => columnDetails.handleDropdownChange(selection, columnDetails.item)}
            allowEmptyOption={allowEmpty}
            concatOptionValues={concatValues}
            disabled={isDisabled}
            inputParams={inputParams}
            initiallyOpen={true}
            onClose={columnDetails.onCloseCallback}
          />
        </Tooltip>
      );
    }

    case 'dependentDropdown': {
      if (!dataApi) return <div />;
      const processedDataApi = processApiParams(dataApi, data ?? {});
      const allowEmpty = isNil(allowEmptyOption) ? true : allowEmptyOption;
      const concatValues = isNil(concatOptionValues) ? false : concatOptionValues;
      const isActive = columnDetails.openDropdownKey === key;
      const displayValue = getDisplayValueForDividedColumnDetails(dataIndex, rawValue, multiSelect, dropdownOptions);

      return isDisabled || !isActive ? (
        <DividedColumnDetailsDropdownRenderer
          key={key}
          id={key}
          displayValue={displayValue}
          inputParams={inputParams}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          onClick={columnDetails.onClickCallback}
          tooltip={''}
        />
      ) : (
        <DependentPropDropdown
          key={key}
          value={value}
          styleId={styleId}
          dataApi={processedDataApi}
          handleDropdownChange={(selection) => columnDetails.handleDropdownChange(selection, columnDetails.item)}
          dataQa={`select-${key}`}
          mandatory={mandatory}
          allowEmptyOption={allowEmpty}
          concatOptionValues={concatValues}
          editable={editable && !isDisabled}
          initiallyOpen={true}
          onClose={columnDetails.onCloseCallback}
        />
      );
    }

    case 'text': {
      const formattedText = renderTextForDividedColumnDetails(value, renderer);
      const isEmptyText = formattedText == null || formattedText === '';
      const isActive = columnDetails.openDropdownKey === key;

      if (!isActive) {
        return (
          <div
            key={key}
            className={classes(styles.dataItem, styles.fakeDropdown)}
            aria-disabled={isDisabled}
            onClick={() => !isDisabled && columnDetails.onClickCallback(key)}
            data-qa="fake-text-input"
          >
            {isEmptyText ? (
              <span className={styles.fakePlaceholder}>Enter value...</span>
            ) : (
              <span className={classes(styles.dataText, styles.editableValue)}>{formattedText}</span>
            )}
          </div>
        );
      }

      return (
        <InputGeneric
          key={key}
          className={classes(styles.dataItem, styles.dataTextInput)}
          editable={true}
          autoFocus={true}
          value={formattedText}
          valid={true}
          disabled={isDisabled}
          onBlurEmpty={() => {
            columnDetails.onCloseCallback();
            if (!isEmptyText) {
              columnDetails.handleDropdownChange({ value: '', label: '' }, columnDetails.item);
            }
          }}
          onBlur={(newText: string) => {
            columnDetails.onCloseCallback();
            if (newText === formattedText) return;
            let result;
            if (valueType === 'number') {
              // TODO: make a configurable parser for this...
              result = parseFloat(newText.replace(/^\$*/g, ''));
            } else {
              result = newText;
            }
            const selection = {
              // mimic dropdown selection structure
              value: result,
              label: newText,
            };
            columnDetails.handleDropdownChange(selection, columnDetails.item);
          }}
          dataQa="divided-column-details-text-input"
        />
      );
    }
    case 'integer': {
      const formattedText = renderTextForDividedColumnDetails(value, renderer);
      const isEmptyText = isNil(value) || value === '';
      const isActive = columnDetails.openDropdownKey === key;

      if (!isActive) {
        return (
          <div
            key={key}
            className={classes(styles.dataItem, styles.fakeDropdown)}
            aria-disabled={isDisabled}
            onClick={() => !isDisabled && columnDetails.onClickCallback(key)}
            data-qa="fake-integer-input"
          >
            {isEmptyText ? (
              <span className={styles.fakePlaceholder}>Enter value...</span>
            ) : (
              <span className={classes(styles.dataText, styles.editableValue)}>{formattedText}</span>
            )}
          </div>
        );
      }

      return (
        <IntegerEditorInline
          key={key}
          value={value}
          inputParams={inputParams}
          onClose={columnDetails.onCloseCallback}
          onCommit={(newValue) => {
            const selection = {
              // mimic dropdown selection structure
              value: newValue,
              label: String(newValue ?? ''),
            };
            columnDetails.handleDropdownChange(selection, columnDetails.item);
          }}
        />
      );
    }
    case 'rangePickerEditor': {
      const { rangePickerData, handleRangePickerChange } = columnDetails;
      // only supported where the host supplies week range data (currently just `HistoryStylePane`)
      if (isNil(rangePickerData) || isNil(handleRangePickerChange)) {
        return renderDCDUneditableComponent();
      }

      const isActive = columnDetails.openDropdownKey === key;

      const { mergedRangeList, allowFrom, allowTo } = rangePickerData;
      const selectionObj = createRowSelectionObj(value, mergedRangeList);

      if (isActive) {
        return (
          <div key={key} className={makeModalOverlay(true)} style={{ width: '100vw', height: '100vh' }}>
            <WeekRangePicker
              autoSelect={true}
              allowFrom={allowFrom}
              allowTo={allowTo}
              dateToNameMap={mergedRangeList}
              initialSelectedArray={!isNil(selectionObj) ? [selectionObj] : undefined}
              onNewWeeksSelectedMulti={(range: NumberedWeek[]) => {
                columnDetails.onCloseCallback();
                handleRangePickerChange(range, columnDetails.item);
              }}
              onCalendarCancel={columnDetails.onCloseCallback}
              selectionOptions={[{ label: text, selected: true }]}
              hideInput={true}
            />
          </div>
        );
      }
      // the below is largely copied from `HistoryStylePane`
      // TODO DRY this out
      const renderedValue = Renderer.renderValue(columnDetails.data!, columnDetails.item as TenantConfigViewItem);
      const decoratedValue = decorateTextByClasses(
        renderedValue,
        (columnDetails.item as TenantConfigViewItem).classes ?? [],
      );
      if (!renderedValue) {
        return <div data-qa={`${columnDetails.item.dataIndex}-value`} dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />;
      } else {
        return (
          <div
            data-qa={`${columnDetails.item.dataIndex}-value`}
            onClick={() => !isDisabled && columnDetails.onClickCallback(key)}
            className={styles.editableValue}
          >
            {decoratedValue}
          </div>
        );
      }
    }
    case 'imageCropper': {
      // The uneditable portion of the block is taken almost whole cloth from `HistoryStylePane`
      // while it *can* be configured in Style Edit it probably shouldn't be
      // TODO DRY this out with the `HistoryStylePane` version
      const isCurrentlyEditing = columnDetails.openDropdownKey === key;
      // @ts-ignore //FIXME
      const imgSrc: string = columnDetails.data[columnDetails.item.dataIndex] || noImage;

      return isCurrentlyEditing ? (
        <ImageCropperModal
          key={key}
          open={columnDetails.openDropdownKey === key}
          passedImage={value as string}
          returnImage={(newImage: string) => {
            const selection = {
              // mimic dropdown selection structure
              value: newImage,
              label: newImage,
            };
            columnDetails.handleDropdownChange(selection, columnDetails.item);
            columnDetails.onCloseCallback();
          }}
          closeModal={columnDetails.onCloseCallback}
        />
      ) : (
        <div
          data-qa={`${columnDetails.item.dataIndex}-value`}
          onClick={() => columnDetails.onClickCallback(key)}
          className={styles.editableValue}
        >
          <CenteredImage
            src={imgSrc}
            width={stylePaneStyles.STYLE_PANE_IMG_WIDTH}
            height={stylePaneStyles.STYLE_PANE_IMG_HEIGHT}
          />
        </div>
      );
    }
    default:
      return null;
  }
};
